import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { postsService } from '../../../services/api/posts';
import { feedService } from '../../../services/api/feed';
import EditorialFeedList from '../../organisms/EditorialFeedList';
import LoadingSpinner from '../../atoms/LoadingSpinner';
import {
    addTrackedPostMutationFields,
    removeTrackedPostMutationFields,
} from '../../../utils/postMutationState';
import useInfiniteScrollObserver from '../../../hooks/useInfiniteScrollObserver';
import './index.css';

export function Feed() {
    const [feedItems, setFeedItems] = useState([]);
    const [isFeedLoading, setIsFeedLoading] = useState(true);
    const feedItemsRef = useRef([]);
    const feedScrollRootRef = useRef(null);
    const postMutationStateRef = useRef({});
    const timelineLoadRequestRef = useRef(0);
    const timelineLoadRef = useRef(null);
    const [hasMoreFeedItems, setHasMoreFeedItems] = useState(false);
    const [isLoadingMoreFeedItems, setIsLoadingMoreFeedItems] = useState(false);
    const currentUser = useSelector((state) => state.auth.user);

    const syncFeedItems = useCallback((nextValue) => {
        const nextItems = typeof nextValue === 'function'
            ? nextValue(feedItemsRef.current)
            : nextValue;
        feedItemsRef.current = nextItems;
        setFeedItems(nextItems);
        return nextItems;
    }, []);

    const resetFeedTimelineLoader = useCallback(() => {
        timelineLoadRef.current = null;
        setHasMoreFeedItems(false);
        setIsLoadingMoreFeedItems(false);
    }, []);

    const fetchFeed = useCallback(async (options = {}) => {
        const requestId = timelineLoadRequestRef.current + 1;
        timelineLoadRequestRef.current = requestId;
        resetFeedTimelineLoader();
        setIsFeedLoading(true);
        try {
            const timelineLoad = await feedService.createFeedLoad(options);
            if (timelineLoadRequestRef.current !== requestId) {
                return;
            }
            timelineLoadRef.current = timelineLoad;
            if (timelineLoad.nextSeedPostMutationState !== undefined) {
                postMutationStateRef.current = timelineLoad.nextSeedPostMutationState;
            }
            syncFeedItems(timelineLoad.items);
            setHasMoreFeedItems(timelineLoad.hasMore === true);
            setIsLoadingMoreFeedItems(false);
        } catch (err) {
            console.error('Error loading feed:', err);
        } finally {
            if (timelineLoadRequestRef.current === requestId) {
                setIsFeedLoading(false);
            }
        }
    }, [resetFeedTimelineLoader, syncFeedItems]);

    const loadMoreFeedItems = useCallback(async () => {
        const timelineLoad = timelineLoadRef.current;
        if (
            !timelineLoad
            || typeof timelineLoad.loadNextPage !== 'function'
            || !hasMoreFeedItems
            || isLoadingMoreFeedItems
        ) {
            return;
        }

        const requestId = timelineLoadRequestRef.current;
        setIsLoadingMoreFeedItems(true);
        try {
            await timelineLoad.loadNextPage((applyUpdate) => {
                if (timelineLoadRequestRef.current === requestId) {
                    return syncFeedItems(applyUpdate);
                }
                return feedItemsRef.current;
            });
            if (timelineLoadRequestRef.current === requestId) {
                syncFeedItems(timelineLoad.items);
                setHasMoreFeedItems(timelineLoad.hasMore === true);
            }
        } catch (err) {
            console.error('Error loading more feed timeline items:', err);
        } finally {
            if (timelineLoadRequestRef.current === requestId) {
                setIsLoadingMoreFeedItems(false);
            }
        }
    }, [hasMoreFeedItems, isLoadingMoreFeedItems, syncFeedItems]);

    const loadMoreSentinelRef = useInfiniteScrollObserver({
        enabled: hasMoreFeedItems,
        hasMore: hasMoreFeedItems,
        isLoading: isLoadingMoreFeedItems,
        onLoadMore: loadMoreFeedItems,
        rootRef: feedScrollRootRef,
    });

    useEffect(() => {
        fetchFeed();
        return () => {
            timelineLoadRequestRef.current += 1;
            timelineLoadRef.current = null;
        };
    }, [fetchFeed]);

    const performDeletePost = useCallback(async (postId) => {
        await postsService.deletePost(postId);
        postMutationStateRef.current = removeTrackedPostMutationFields(
            postMutationStateRef.current,
            postId,
        );
        syncFeedItems((prev) => prev.filter((post) => String(post.id) !== String(postId)));
    }, [syncFeedItems]);

    const handleDeleteEntity = useCallback(async (itemId) => {
        if (!itemId) {
            return;
        }
        const targetId = String(itemId);
        syncFeedItems((previousItems) => (
            Array.isArray(previousItems)
                ? previousItems.filter((item) => String(item?.id) !== targetId)
                : []
        ));
        try {
            await fetchFeed({
                requirePostSource: true,
                seedItems: () => feedItemsRef.current,
                seedPostMutationState: () => postMutationStateRef.current,
            });
        } catch (refreshError) {
            console.error('Error refreshing feed after entity deletion:', refreshError);
        }
    }, [fetchFeed, syncFeedItems]);

    const handlePostMetaChange = useCallback((postId, partial) => {
        postMutationStateRef.current = addTrackedPostMutationFields(
            postMutationStateRef.current,
            postId,
            partial,
        );
        syncFeedItems((prev) => prev.map((post) => (
            post.id === postId ? { ...post, ...partial } : post
        )));
    }, [syncFeedItems]);

    const items = useMemo(() => feedItems, [feedItems]);
    const shouldShowInitialLoader = isFeedLoading && items.length === 0;

    return (
        <div className="feed-container" ref={feedScrollRootRef}>
            {shouldShowInitialLoader ? (
                <div className="feed-container__loading" aria-live="polite">
                    <LoadingSpinner size={40} />
                </div>
            ) : (
                <EditorialFeedList
                    items={items}
                    currentUserId={currentUser?.id}
                    onPostMetaChange={handlePostMetaChange}
                    onDeletePost={performDeletePost}
                    onDeleteEntity={handleDeleteEntity}
                    hasMore={hasMoreFeedItems}
                    isLoadingMore={isLoadingMoreFeedItems}
                    loadMore={loadMoreFeedItems}
                    loadMoreSentinelRef={loadMoreSentinelRef}
                    virtualizationRootRef={feedScrollRootRef}
                />
            )}
        </div>
    );
}
